import { useState } from 'react'
import { ethers, BigNumber } from 'ethers'
import invariant from 'tiny-invariant'
import { TicketIcon } from '@heroicons/react/24/outline'

import { CheckoutSession, Network, Coupon } from '../../types'
import { ButtonPill } from '../../components'
import { API_KEY } from '../../constants'
import { useAccount, useSigner, useNetwork } from 'wagmi'

const ABI = [
  {
    inputs: [
      {
        internalType: 'string',
        name: 'name',
        type: 'string',
      },
      {
        internalType: 'string',
        name: 'symbol',
        type: 'string',
      },
    ],
    stateMutability: 'nonpayable',
    type: 'constructor',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'tokenId',
        type: 'uint256',
      },
    ],
    name: 'burn',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'to',
        type: 'address',
      },
      {
        internalType: 'string',
        name: '_tokenURI',
        type: 'string',
      },
      {
        internalType: 'uint256',
        name: '_discount',
        type: 'uint256',
      },
    ],
    name: 'mint',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          {
            internalType: 'address',
            name: 'redeemer',
            type: 'address',
          },
          {
            internalType: 'string',
            name: 'uri',
            type: 'string',
          },
          {
            internalType: 'uint256',
            name: 'bp',
            type: 'uint256',
          },
          {
            internalType: 'bytes',
            name: 'signature',
            type: 'bytes',
          },
        ],
        internalType: 'struct Coupon.CouponVoucher',
        name: 'voucher',
        type: 'tuple',
      },
    ],
    name: 'mintCoupon',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
]

enum Status {
  LOADING,
  READY,
  ERROR,
}

interface Props {
  hasWalletInfoLoaded: boolean
  checkoutSession?: CheckoutSession
}

export default function CouponsList({
  hasWalletInfoLoaded,
  checkoutSession,
}: Props) {
  const [status, setStatus] = useState(Status.READY)
  const [minting, setMinting] = useState(false)

  const [isBurning, setIsBurning] = useState(false)
  const [burningId, setBurningId] = useState(-1)

  const [burnedList, setBurnedList] = useState<number[]>([]) // keep track of token id burns in progress

  const { chain } = useNetwork()
  const { address, isConnected } = useAccount()
  const { data: signerWalletConnect, isError, isLoading } = useSigner()

  async function connectedWalletInfo() {
    try {
      if (isConnected) {
        if (!address) throw new Error('no wallet connect address')
        if (!signerWalletConnect) throw new Error('no signerWalletConnect :(')

        return {
          address,
          chainId: `0x${chain?.id.toString(16)}`,
          signer: signerWalletConnect,
        }
      } else {
        if (!(window as any).ethereum) throw new Error('no window.ethereum')

        const provider = new ethers.providers.Web3Provider(
          (window as any).ethereum,
          'any',
        )

        if (!provider) {
          throw new Error('connect wallet webview - no provider')
        }
        const signer = provider.getSigner()
        if (!signer) throw new Error('no signer :(')

        const accounts = await (window as any).ethereum.request({
          method: 'eth_requestAccounts',
        })
        if (!accounts[0]) throw new Error('connect wallet webview - no account')

        const chainId = (await (window as any).ethereum.request({
          method: 'eth_chainId',
        })) as string

        if (!chainId) throw new Error('no chain_id')

        return {
          address: accounts[0],
          chainId,
          signer,
        }
      }
    } catch (err) {
      throw new Error('getting connected wallet info')
    }
  }

  async function handleBurn(coupon: Coupon) {
    setBurningId(coupon.token_id)

    const { address, chainId, signer } = await connectedWalletInfo()

    try {
      if (!signer) throw new Error('no signerWalletConnect :(')
      if (!chainId) throw new Error('no chain?.id')

      const contract = new ethers.Contract(coupon.contract_address, ABI, signer)
      const tx = await contract.burn(coupon.token_id)

      setBurnedList([...burnedList, coupon.token_id])
      setStatus(Status.LOADING)
      setIsBurning(true)

      const res = await fetch(
        `https://payments-api-c560.onrender.com/api/v1/payment_requests/${checkoutSession?.id}/coupon`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'X-API-KEY': API_KEY,
          },
          body: JSON.stringify({
            coupon_burn_tx: tx,
          }),
        },
      )

      if (!res.ok) throw new Error('burning coupon failed to fetch')

      setStatus(Status.READY)

      await tx.wait()

      setBurningId(-1)
      setIsBurning(false)

      //
    } catch (err) {
      console.error(err)
      setBurningId(-1)
      setIsBurning(false)

      setStatus(Status.READY)
    }
  }

  if (!checkoutSession) return null

  async function mintTestCoupon() {
    setMinting(true)

    const { address, chainId, signer } = await connectedWalletInfo()

    try {
      if (!signer) throw new Error('no signerWalletConnect :(')
      if (!chainId) throw new Error('no chain?.id')
      if (!address) throw new Error('no address')

      const res = await fetch(
        `https://payments-api-c560.onrender.com/api/v1/coupon/TEST/mint`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'X-API-KEY': API_KEY,
          },
          body: JSON.stringify({
            receiver: address,
            chain_id: BigNumber.from(chainId).toNumber(),
          }),
        },
      )
      const data = await res.json()

      if (!res.ok) {
        console.error(JSON.stringify(data, null, 4))
        throw new Error('failed to fetch ')
      }

      const tx = await signer.sendTransaction(data.tx)

      setStatus(Status.LOADING)

      await tx.wait()

      setStatus(Status.READY)
      setMinting(false)
    } catch (err) {
      console.error(err)
      if ((err as any)?.code !== 'ACTION_REJECTED') {
        alert(err)
      }
      setMinting(false)
    }
  }

  return (
    <div>
      <div className="flex">
        <TicketIcon className="w-6 h-6 mr-2 text-greeeen" />
        <h3 className="font-semibold mb-4">Coupons</h3>
      </div>

      <ul>
        {(!hasWalletInfoLoaded || status === Status.LOADING) && (
          <div className="flex flex-row justify-between">
            <div className="flex flex-row">
              <div className="animate-pulse h-11 w-20 bg-neutral-700 rounded mr-3"></div>
              <div className="flex flex-col">
                <div className="animate-pulse h-4 w-10 bg-neutral-700 rounded mb-2"></div>

                <div className="animate-pulse h-4 w-28 bg-neutral-700 rounded "></div>
              </div>
            </div>

            <div className="animate-pulse h-10 w-28 bg-neutral-700 rounded-3xl "></div>
          </div>
        )}

        {hasWalletInfoLoaded &&
          !checkoutSession?.wallet?.coupons?.filter(
            (coupon) => !burnedList.includes(coupon.token_id),
          )?.length &&
          status !== Status.LOADING && (
            <li>
              <div className="flex flex-row justify-between items-center">
                <p className="text-sm mb-5 text-neutral-300">
                  You don't have any coupons
                </p>
                <ButtonPill onClick={mintTestCoupon}>
                  {!minting ? (
                    'Mint test coupon'
                  ) : (
                    <>
                      <svg
                        aria-hidden="true"
                        role="status"
                        className="inline w-4 h-4 mr-3 text-gray-200 animate-spin dark:text-gray-600"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="#1C64F2"
                        />
                      </svg>
                      Waiting
                    </>
                  )}
                </ButtonPill>
              </div>
            </li>
          )}

        {hasWalletInfoLoaded &&
          status !== Status.LOADING &&
          checkoutSession?.wallet?.coupons
            ?.filter((coupon) =>
              isBurning ? coupon.token_id !== burningId : true,
            )
            .filter((coupon) => !burnedList.includes(coupon.token_id))
            .map((coupon, i) => (
              <li key={coupon.token_id}>
                <div className="flex justify-between">
                  <div className="flex">
                    <img
                      src={coupon.image}
                      className="mr-3"
                      style={{ width: '70px' }}
                    />
                    <div className="flex flex-col">
                      <p className="text-neutral-400">{coupon.name}</p>
                      <p className="text-neutral-400">
                        Discount {coupon.bp / 100}%
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-center items-center">
                    <ButtonPill onClick={() => handleBurn(coupon)}>
                      {burningId !== coupon.token_id ? (
                        'Apply'
                      ) : (
                        <>
                          <svg
                            aria-hidden="true"
                            role="status"
                            className="inline w-4 h-4 mr-3 text-gray-200 animate-spin dark:text-gray-600"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                            <path
                              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                              fill="#1C64F2"
                            />
                          </svg>
                          Waiting
                        </>
                      )}
                    </ButtonPill>
                  </div>
                </div>
              </li>
            ))}
      </ul>
    </div>
  )
}
